import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { nw } from "@/components/landing/landing-data";

/**
 * Copy for the "UnfoldWin turns reflection into momentum." section, animated to act out
 * its own sentence (homepage only). Plays once when it first comes into view:
 *
 *   eyebrow   – decodes itself, letters resolving from random glyphs, left to right
 *   UnfoldWin / turns – rise into focus word by word
 *   reflection – starts upside down and faint, like a reflection, then turns upright
 *   into momentum. – letters arrive from the left, skewed with speed, each a little sooner
 *                    than the last, so the phrase accelerates; a band of light then sweeps
 *                    across it, recurring while the section is on screen
 *   paragraph – fades up last
 *
 * Assistive tech gets the plain sentences (the split/animated copies are aria-hidden).
 * With reduced motion everything renders in its final state.
 */

const EYEBROW = "Guided by our engineered AI";
const MOMENTUM = "into momentum.";
const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/** Letters resolve left to right; unresolved ones cycle through random glyphs. */
function DecodeText({ text, play }: { text: string; play: boolean }) {
  const [out, setOut] = useState(play ? "" : text);
  useEffect(() => {
    if (!play) {
      setOut(text);
      return;
    }
    const start = performance.now();
    const settleEach = 32; // ms per letter
    const id = window.setInterval(() => {
      const settled = Math.floor((performance.now() - start - 120) / settleEach);
      if (settled >= text.length) {
        setOut(text);
        window.clearInterval(id);
        return;
      }
      setOut(
        text
          .split("")
          .map((ch, i) =>
            i < settled || ch === " " ? ch : i < settled + 6 ? GLYPHS[Math.floor(Math.random() * GLYPHS.length)] : " ",
          )
          .join(""),
      );
    }, 40);
    return () => window.clearInterval(id);
  }, [play, text]);
  return <>{out}</>;
}

/** Delay (s) before each letter of "into momentum." — gaps shrink, so the phrase gathers speed. */
const momentumDelays = (() => {
  const out: number[] = [];
  let t = 0;
  let gap = 0.075;
  for (let i = 0; i < MOMENTUM.length; i++) {
    out.push(t);
    t += gap;
    gap = Math.max(0.018, gap * 0.84);
  }
  return out;
})();

const rise = {
  hidden: { opacity: 0, y: "0.35em", filter: "blur(10px)" },
  shown: { opacity: 1, y: "0em", filter: "blur(0px)" },
};

export function QuestionsCopy({ reduce, active }: { reduce: boolean; active: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const play = inView && !reduce;
  const shown = inView || reduce;
  const state = shown ? "shown" : "hidden";

  // Shimmer across "into momentum." once the letters have landed, then every ~9s on screen.
  const [shine, setShine] = useState(0);
  useEffect(() => {
    if (!play || !active) return;
    const first = window.setTimeout(() => setShine((n) => n + 1), 2200);
    const every = window.setInterval(() => setShine((n) => n + 1), 9000);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(every);
    };
  }, [play, active]);

  return (
    <div ref={ref} className="flex flex-col gap-5">
      <p className="lp-eyebrow" aria-label={EYEBROW}>
        <span aria-hidden className="inline-block min-h-[1em] whitespace-pre">
          {shown ? <DecodeText text={EYEBROW} play={play} /> : " "}
        </span>
      </p>

      <h2 id="lp-questions-title" className="lp-h2" aria-label="UnfoldWin turns reflection into momentum.">
        <span aria-hidden>
          {(["UnfoldWin", "turns"] as const).map((word, i) => (
            <span key={word}>
              <motion.span
                className="inline-block"
                variants={rise}
                initial={reduce ? false : "hidden"}
                animate={state}
                transition={{ duration: 0.9, ease: [0.16, 0.8, 0.2, 1], delay: reduce ? 0 : 0.35 + i * 0.14 }}
              >
                {word}
              </motion.span>{" "}
            </span>
          ))}
          <motion.span
            className="inline-block"
            style={{ transformPerspective: 700, transformOrigin: "50% 62%" }}
            initial={reduce ? false : { opacity: 0, rotateX: 180, filter: "blur(6px)" }}
            animate={shown ? { opacity: 1, rotateX: 0, filter: "blur(0px)" } : undefined}
            transition={{
              rotateX: { type: "spring", stiffness: 70, damping: 14, delay: reduce ? 0 : 0.7 },
              opacity: { duration: 0.7, delay: reduce ? 0 : 0.5 },
              filter: { duration: 0.8, delay: reduce ? 0 : 0.8 },
            }}
          >
            reflection
          </motion.span>{" "}
          <span className="lp-accent relative inline-block whitespace-nowrap">
            {MOMENTUM.split("").map((ch, i) => (
              <motion.span
                key={i}
                className="inline-block whitespace-pre"
                initial={reduce ? false : { opacity: 0, x: "-0.6em", skewX: -14 }}
                animate={shown ? { opacity: 1, x: "0em", skewX: 0 } : undefined}
                transition={{
                  duration: 0.55,
                  ease: [0.2, 0.9, 0.25, 1],
                  delay: reduce ? 0 : 1.25 + momentumDelays[i],
                }}
              >
                {ch}
              </motion.span>
            ))}
            {/* The light band: a clipped copy of the phrase with a moving highlight. */}
            {play && shine > 0 && (
              <motion.span
                key={shine}
                aria-hidden
                className="lp-shine pointer-events-none absolute inset-0 whitespace-pre"
                initial={{ backgroundPosition: "140% 0" }}
                animate={{ backgroundPosition: "-40% 0" }}
                transition={{ duration: 1.3, ease: [0.4, 0, 0.2, 1] }}
              >
                {MOMENTUM.split("").map((ch, i) => (
                  <span key={i} className="inline-block whitespace-pre">
                    {ch}
                  </span>
                ))}
              </motion.span>
            )}
          </span>
        </span>
      </h2>

      <motion.p
        className="lp-lede"
        initial={reduce ? false : { opacity: 0, y: 14, filter: "blur(4px)" }}
        animate={shown ? { opacity: 1, y: 0, filter: "blur(0px)" } : undefined}
        transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1], delay: reduce ? 0 : 1.75 }}
      >
        {nw(
          "Move beyond surface-level takeaways. Get deeper insights, sharper perspective, and prompts that turn content into real change.",
        )}
      </motion.p>
    </div>
  );
}
